import { useId } from "react";
import { cn } from "../utils/cn";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface RadioOption {
  /** Primary label for this radio option */
  label: string;
  /** Internal value passed to onChange when selected */
  value: string;
  /** Supplementary text shown below the label */
  description?: string;
  /** Prevents this option from being selected */
  disabled?: boolean;
}

export interface RadioGroupProps {
  /** All selectable options */
  options: RadioOption[];
  /** Currently selected value */
  value: string;
  /** Called with the new value when selection changes */
  onChange: (value: string) => void;
  /** Label rendered above the group */
  label?: string;
  /** Lays options out side-by-side or stacked */
  orientation?: "horizontal" | "vertical";
  /** default = radio + text inline; card = each option in a bordered card */
  variant?: "default" | "card";
  /** Additional className on the root fieldset */
  className?: string;
}

// ── Dot icon ─────────────────────────────────────────────────────────────────

function RadioDot() {
  return (
    <div
      className="w-2 h-2 rounded-full bg-brand-purple animate-scale-in"
      aria-hidden="true"
    />
  );
}

// ── Component ─────────────────────────────────────────────────────────────────

export function RadioGroup({
  options,
  value,
  onChange,
  label,
  orientation = "vertical",
  variant = "default",
  className,
}: RadioGroupProps) {
  const groupId = useId();

  const isCard = variant === "card";

  return (
    <fieldset
      role="radiogroup"
      aria-labelledby={label ? `${groupId}-legend` : undefined}
      className={cn("flex flex-col gap-2 border-none p-0 m-0", className)}
    >
      {label && (
        <legend
          id={`${groupId}-legend`}
          className="text-sm font-medium text-content-primary mb-1"
        >
          {label}
        </legend>
      )}

      <div
        className={cn(
          "flex gap-2",
          orientation === "vertical" ? "flex-col" : "flex-row flex-wrap",
        )}
      >
        {options.map(option => {
          const optId = `${groupId}-${option.value}`;
          const isSelected = value === option.value;

          if (isCard) {
            return (
              <label
                key={option.value}
                htmlFor={optId}
                className={cn(
                  "flex items-start gap-3 p-4 rounded-card border-2 transition-all duration-150",
                  option.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
                  isSelected
                    ? [
                        "border-brand-purple dark:border-violet-500",
                        "bg-brand-purple-light dark:bg-violet-500/12",
                      ].join(" ")
                    : [
                        "border-border-default dark:border-border-strong",
                        "bg-surface dark:bg-surface-raised",
                        !option.disabled &&
                          "hover:border-brand-purple/50 dark:hover:border-violet-500",
                      ].join(" "),
                )}
              >
                {/* Native radio — hidden, keyboard accessible */}
                <input
                  type="radio"
                  id={optId}
                  name={groupId}
                  value={option.value}
                  checked={isSelected}
                  disabled={option.disabled}
                  onChange={() => !option.disabled && onChange(option.value)}
                  className="sr-only peer"
                />

                {/* Custom radio circle */}
                <div
                  aria-hidden="true"
                  className={cn(
                    "mt-0.5 w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0",
                    "transition-all duration-150",
                    "peer-focus-visible:ring-2 peer-focus-visible:ring-brand-purple",
                    "peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface",
                    isSelected
                      ? "border-brand-purple"
                      : "border-border-strong dark:border-border-strong",
                  )}
                >
                  {isSelected && <RadioDot />}
                </div>

                {/* Text */}
                <div className="flex flex-col gap-0.5">
                  <span
                    className={cn(
                      "text-base font-medium leading-tight",
                      isSelected
                        ? "text-brand-purple dark:text-violet-400"
                        : "text-content-primary",
                    )}
                  >
                    {option.label}
                  </span>
                  {option.description && (
                    <span className="text-sm text-content-secondary leading-snug">
                      {option.description}
                    </span>
                  )}
                </div>
              </label>
            );
          }

          // Default variant
          return (
            <label
              key={option.value}
              htmlFor={optId}
              className={cn(
                "inline-flex items-start gap-2.5 group",
                option.disabled ? "cursor-not-allowed opacity-60" : "cursor-pointer",
              )}
            >
              <input
                type="radio"
                id={optId}
                name={groupId}
                value={option.value}
                checked={isSelected}
                disabled={option.disabled}
                onChange={() => !option.disabled && onChange(option.value)}
                className="sr-only peer"
              />

              {/* Custom radio circle */}
              <div
                aria-hidden="true"
                className={cn(
                  "mt-px w-4 h-4 rounded-full border-2 flex items-center justify-center shrink-0",
                  "transition-all duration-150",
                  "peer-focus-visible:ring-2 peer-focus-visible:ring-brand-purple",
                  "peer-focus-visible:ring-offset-2 peer-focus-visible:ring-offset-surface",
                  isSelected
                    ? "border-brand-purple"
                    : [
                        "border-border-strong",
                        !option.disabled &&
                          "group-hover:border-brand-purple/60 dark:group-hover:border-violet-500/70",
                      ].join(" "),
                )}
              >
                {isSelected && <RadioDot />}
              </div>

              {/* Text */}
              <div className="flex flex-col gap-0.5 leading-tight">
                <span className="text-base font-medium text-content-primary">
                  {option.label}
                </span>
                {option.description && (
                  <span className="text-sm text-content-secondary">
                    {option.description}
                  </span>
                )}
              </div>
            </label>
          );
        })}
      </div>
    </fieldset>
  );
}

RadioGroup.displayName = "RadioGroup";
